const getNodeText = node => {
  if (node === null || node === undefined || typeof node === "boolean")
    return "";
  if (typeof node === "string" || typeof node === "number") return `${node}`;
  if (Array.isArray(node)) return node.map(getNodeText).join("");
  return getNodeText(node?.props?.children);
};

const getOptionText = option =>
  getNodeText(option?.label).replace(/\s+/g, " ").trim() ||
  (typeof option?.title === "string" ? option.title.trim() : "");

const isSubsequence = (needle, haystack) =>
  [...needle].reduce(
    (position, character) =>
      position < 0 ? position : haystack.indexOf(character, position) + 1 || -1,
    0,
  ) >= 0;

// Optimal string alignment distance, so a swapped pair of letters costs one edit.
const getEditDistance = (source, target) => {
  const firstRow = Array.from({ length: target.length + 1 }, (_, index) => index);
  const rows = [...source].reduce(
    (previousRows, sourceCharacter, sourceIndex) => {
      const previousRow = previousRows[previousRows.length - 1];
      const rowBefore = previousRows[previousRows.length - 2];
      const row = [...target].reduce(
        (currentRow, targetCharacter, targetIndex) => {
          const substitutionCost = sourceCharacter === targetCharacter ? 0 : 1;
          const isTransposition =
            sourceIndex > 0 &&
            targetIndex > 0 &&
            sourceCharacter === target[targetIndex - 1] &&
            source[sourceIndex - 1] === targetCharacter;
          return [
            ...currentRow,
            Math.min(
              previousRow[targetIndex + 1] + 1,
              currentRow[targetIndex] + 1,
              previousRow[targetIndex] + substitutionCost,
              isTransposition ? rowBefore[targetIndex - 1] + 1 : Infinity,
            ),
          ];
        },
        [sourceIndex + 1],
      );
      return [...previousRows, row];
    },
    [firstRow],
  );
  return rows[rows.length - 1][target.length];
};

const minimumTypoTermLength = 4;

const isTypoOfWordPrefix = (term, word) =>
  term.length >= minimumTypoTermLength &&
  [term.length - 1, term.length, term.length + 1].some(
    length => getEditDistance(term, word.slice(0, length)) <= 1,
  );

const removeSpaces = value => value.replace(/ /g, "");

const scoreOptionText = (text, query) => {
  const normalisedText = text.toLowerCase();
  const normalisedQuery = query.trim().toLowerCase().replace(/\s+/g, " ");
  const terms = normalisedQuery.split(" ").filter(Boolean);
  const words = normalisedText.split(" ");
  const compactText = removeSpaces(normalisedText);
  const compactQuery = removeSpaces(normalisedQuery);
  const initials = words.map(word => word[0]).join("");

  if (terms.length === 0) return 1;
  if (compactText === compactQuery) return 7;
  if (normalisedText.startsWith(normalisedQuery)) return 6;
  if (compactText.startsWith(compactQuery)) return 5;
  if (terms.every(term => words.some(word => word.startsWith(term)))) return 4;
  if (compactQuery.length > 1 && initials.startsWith(compactQuery)) return 4;
  if (compactText.includes(compactQuery)) return 3;
  if (terms.every(term => normalisedText.includes(term))) return 3;
  if (
    terms.every(term =>
      words.some(
        word => word.startsWith(term) || isTypoOfWordPrefix(term, word),
      ),
    )
  )
    return 2;
  if (isSubsequence(compactQuery, compactText)) return 1;
  return 0;
};

const compareEntries = (first, second) =>
  second.score - first.score ||
  Number(Boolean(first.option?.disabled)) -
    Number(Boolean(second.option?.disabled)) ||
  first.index - second.index;

const filterCommandPaletteOptions = (options = [], query = "") =>
  options
    .map((option, index) => ({ option, index, text: getOptionText(option) }))
    .filter(entry => entry.text)
    .map(entry => ({ ...entry, score: scoreOptionText(entry.text, query) }))
    .filter(entry => entry.score > 0)
    .sort(compareEntries);

const getCommandPaletteCompletion = (entries = [], query = "") => {
  const normalisedQuery = query.toLowerCase();
  if (!normalisedQuery.trim()) return null;

  const enabledEntries = entries.filter(entry => !entry.option?.disabled);
  const prefixMatch = enabledEntries.find(
    entry =>
      entry.text.length > query.length &&
      entry.text.toLowerCase().startsWith(normalisedQuery),
  );
  if (prefixMatch) return `${query}${prefixMatch.text.slice(query.length)}`;

  const compactQuery = removeSpaces(normalisedQuery);
  const compactMatch = enabledEntries.find(entry =>
    removeSpaces(entry.text.toLowerCase()).startsWith(compactQuery),
  );
  return compactMatch && compactMatch.text !== query ? compactMatch.text : null;
};

const isCompletionOfQuery = (completion, query) =>
  Boolean(completion) &&
  completion.toLowerCase().startsWith(query.toLowerCase());

const isApplePlatform = (navigatorObject = globalThis.navigator) =>
  /mac|iphone|ipad|ipod/i.test(
    navigatorObject?.userAgentData?.platform ||
      navigatorObject?.platform ||
      navigatorObject?.userAgent ||
      "",
  );

const isCommandPaletteShortcut = (event, applePlatform) =>
  event?.key?.toLowerCase() === "p" &&
  !event.altKey &&
  !event.shiftKey &&
  Boolean(
    applePlatform
      ? event.metaKey && !event.ctrlKey
      : event.ctrlKey && !event.metaKey,
  );

// Browsers send mousemove without movement when content shifts under a still
// cursor (opening, filtering, scrolling), so only a new position counts.
const hasPointerMoved = (previousPosition, position) =>
  Boolean(previousPosition) &&
  (previousPosition.x !== position.x || previousPosition.y !== position.y);

const getCommandPaletteShortcutLabel = applePlatform =>
  applePlatform ? "⌘P" : "Ctrl+P";

const getCommandPaletteKeyShortcuts = applePlatform =>
  applePlatform ? "Meta+P" : "Control+P";

export {
  filterCommandPaletteOptions,
  getCommandPaletteCompletion,
  getCommandPaletteKeyShortcuts,
  getCommandPaletteShortcutLabel,
  getEditDistance,
  getOptionText,
  hasPointerMoved,
  isCompletionOfQuery,
  isApplePlatform,
  isCommandPaletteShortcut,
};
